import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import StarRating from "../components/ui/StarRating";
import { useLanguage } from "../contexts/LanguageContext";
import logger from "../utils/logger.js";
import {
  formatConsultationCurrency,
  formatConsultationDateTime,
  getConsultationDurationLabel,
  getConsultationTitle,
  getMyConsultations,
  submitConsultationReview,
} from "../api/consultations";

function getStatusMeta(status, isAr) {
  const map = {
    confirmed: {
      className: "text-bg-success",
      label: isAr ? "مؤكدة" : "Confirmed",
    },
    pending: {
      className: "text-bg-warning",
      label: isAr ? "قيد المراجعة" : "Pending",
    },
    completed: {
      className: "text-bg-primary",
      label: isAr ? "مكتملة" : "Completed",
    },
    cancelled: {
      className: "text-bg-danger",
      label: isAr ? "ملغية" : "Cancelled",
    },
  };

  return map[String(status || "").toLowerCase()] || {
    className: "text-bg-secondary",
    label: status || (isAr ? "غير معروف" : "Unknown"),
  };
}

function createReviewDraft() {
  return {
    rating: "5",
    comment: "",
  };
}

export default function MyConsultations({ embedded = false }) {
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [actionBookingId, setActionBookingId] = useState(0);
  const [reviewDrafts, setReviewDrafts] = useState({});

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError("");
        setNotice("");
        const list = await getMyConsultations();
        if (active) {
          setBookings(list);
        }
      } catch (err) {
        logger.error("My consultations error", err);
        if (active) {
          setError(isAr ? "تعذر تحميل استشاراتك." : "Unable to load your consultations.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      active = false;
    };
  }, [isAr]);

  function updateReviewDraft(bookingId, field, value) {
    setReviewDrafts((current) => ({
      ...current,
      [bookingId]: {
        ...(current[bookingId] || createReviewDraft()),
        [field]: value,
      },
    }));
  }

  async function handleSubmitReview(booking) {
    const draft = reviewDrafts[booking.id] || createReviewDraft();

    try {
      setActionBookingId(booking.id);
      setError("");
      setNotice("");

      const review = await submitConsultationReview(booking.consultation.id, {
        booking_id: booking.id,
        rating: Number(draft.rating || 5),
        comment: draft.comment.trim() || undefined,
      });

      setBookings((current) =>
        current.map((entry) =>
          entry.id === booking.id
            ? {
                ...entry,
                can_review: false,
                review,
                consultation: {
                  ...entry.consultation,
                  reviews_count: Number(entry.consultation?.reviews_count || 0) + 1,
                  average_rating:
                    Number(entry.consultation?.reviews_count || 0) > 0
                      ? Number((
                          ((Number(entry.consultation?.average_rating || 0) *
                            Number(entry.consultation?.reviews_count || 0)) +
                            Number(review?.rating || 0)) /
                          (Number(entry.consultation?.reviews_count || 0) + 1)
                        ).toFixed(2))
                      : Number(review?.rating || 0),
                },
              }
            : entry,
        ),
      );
      setReviewDrafts((current) => ({ ...current, [booking.id]: createReviewDraft() }));
      setNotice(isAr ? "تم إرسال تقييم الاستشارة بنجاح." : "Consultation review submitted successfully.");
    } catch (err) {
      logger.error("Consultation review error", err);
      setError(err?.message || (isAr ? "تعذر إرسال تقييم الاستشارة." : "Unable to submit the consultation review."));
    } finally {
      setActionBookingId(0);
    }
  }

  return (
    <div className={embedded ? "" : "container py-4 py-lg-5"} dir={isAr ? "rtl" : "ltr"}>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <h1 className={`${embedded ? "h3" : ""} fw-bold mb-1`}>
            {isAr ? "استشاراتي" : "My Consultations"}
          </h1>
          <p className="text-muted mb-0">
            {isAr
              ? "كل الجلسات التي حجزتها مع حالتها، الموعد، والرابط إذا كان متاحًا."
              : "All booked sessions with their status, schedule, and meeting link when available."}
          </p>
        </div>
        <Link to="/consulting" className="btn btn-outline-dark rounded-pill px-4">
          {isAr ? "تصفح الاستشارات" : "Browse Consultations"}
        </Link>
      </div>

      {notice ? <div className="alert alert-success rounded-4">{notice}</div> : null}

      {loading ? (
        <div className="py-5 text-center">
          <div className="spinner-border text-primary mb-3" role="status" />
          <p className="mb-0">{isAr ? "جارِ تحميل استشاراتك..." : "Loading your consultations..."}</p>
        </div>
      ) : error ? (
        <div className="alert alert-danger rounded-4">{error}</div>
      ) : bookings.length === 0 ? (
        <div className="card border-0 shadow-sm rounded-4">
          <div className="card-body py-5 text-center">
            <h2 className="h4 fw-bold mb-2">
              {isAr ? "لا توجد حجوزات بعد" : "You do not have any bookings yet"}
            </h2>
            <p className="text-muted mb-4">
              {isAr
                ? "ابدأ من صفحة الاستشارات واختر الجلسة المناسبة لك."
                : "Start from the consultations page and choose the session that fits you."}
            </p>
            <Link to="/consulting" className="btn btn-primary rounded-pill px-4">
              {isAr ? "عرض الاستشارات" : "View Consultations"}
            </Link>
          </div>
        </div>
      ) : (
        <div className="row g-4">
          {bookings.map((booking) => {
            const consultation = booking.consultation || {};
            const statusMeta = getStatusMeta(booking.status, isAr);
            const reviewDraft = reviewDrafts[booking.id] || createReviewDraft();
            const review = booking.review || null;

            return (
              <div className="col-12 col-lg-6" key={booking.id}>
                <div className="card border-0 shadow-sm rounded-4 h-100 overflow-hidden">
                  <div className="row g-0 h-100">
                    <div className="col-md-4">
                      <img
                        src={
                          consultation?.cover_image ||
                          "https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=1200&q=80"
                        }
                        alt={getConsultationTitle(consultation, isAr)}
                        className="course-my-card__image"
                      />
                    </div>

                    <div className="col-md-8">
                      <div className="card-body h-100 d-flex flex-column">
                        <div className="d-flex flex-wrap gap-2 mb-2">
                          <span className={`badge ${statusMeta.className}`}>{statusMeta.label}</span>
                          <span className="badge text-bg-light">
                            {getConsultationDurationLabel(
                              {
                                duration_minutes:
                                  booking?.duration_minutes || consultation?.duration_minutes,
                              },
                              isAr
                            )}
                          </span>
                        </div>

                        <h2 className="h5 fw-bold mb-2">
                          {getConsultationTitle(consultation, isAr)}
                        </h2>

                        <div className="small text-muted d-grid gap-2 mb-3">
                          <div className="d-flex flex-wrap align-items-center gap-3">
                            <StarRating value={consultation?.average_rating || 0} size={16} showValue />
                            <span>
                              {Number(consultation?.reviews_count || 0)} {isAr ? "تقييم منشور" : "published reviews"}
                            </span>
                          </div>

                          <div>
                            {isAr ? "السعر:" : "Price:"}{" "}
                            {Number(booking?.price || 0) > 0
                              ? formatConsultationCurrency(
                                  booking.price,
                                  booking.currency || "USD"
                                )
                              : isAr
                                ? "مجاني"
                                : "Free"}
                          </div>

                          <div>
                            {isAr ? "الموعد:" : "Scheduled:"}{" "}
                            {booking?.scheduled_at
                              ? formatConsultationDateTime(booking.scheduled_at, lang)
                              : isAr
                                ? "سيتم التنسيق لاحقًا"
                                : "To be coordinated later"}
                          </div>

                          <div>
                            {isAr ? "طريقة الاجتماع:" : "Meeting type:"}{" "}
                            {consultation?.meeting_provider || (isAr ? "أونلاين" : "Online")}
                          </div>
                        </div>

                        {booking?.admin_notes ? (
                          <div className="alert alert-light border rounded-4 small mb-3">
                            <strong>{isAr ? "ملاحظة الإدارة:" : "Admin note:"}</strong>{" "}
                            {booking.admin_notes}
                          </div>
                        ) : null}

                        <div className="d-flex flex-wrap gap-2 mt-auto">
                          <Link
                            to={`/consulting/${consultation?.slug || consultation?.id}`}
                            className="btn btn-primary rounded-pill px-4"
                          >
                            {isAr ? "تفاصيل الاستشارة" : "Consultation Details"}
                          </Link>

                          {booking?.meeting_link ? (
                            <a
                              href={booking.meeting_link}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-outline-dark rounded-pill"
                            >
                              {isAr ? "فتح رابط الاجتماع" : "Open Meeting Link"}
                            </a>
                          ) : null}
                        </div>

                        <div className="border rounded-4 p-3 mt-3 bg-light-subtle">
                          <h3 className="h6 fw-bold mb-2">{isAr ? "التقييم" : "Review"}</h3>
                          {review ? (
                            <div className="d-grid gap-2">
                              <StarRating value={review.rating || 0} size={18} showValue />
                              <div className="text-muted small">
                                {review.comment || (isAr ? "بدون تعليق إضافي." : "No additional comment.")}
                              </div>
                            </div>
                          ) : booking.can_review ? (
                            <div className="d-grid gap-2">
                              <select
                                className="form-select"
                                value={reviewDraft.rating}
                                onChange={(event) => updateReviewDraft(booking.id, "rating", event.target.value)}
                                disabled={actionBookingId === booking.id}
                              >
                                {[5, 4, 3, 2, 1].map((rating) => (
                                  <option key={rating} value={rating}>
                                    {rating}/5
                                  </option>
                                ))}
                              </select>
                              <textarea
                                className="form-control"
                                rows="3"
                                value={reviewDraft.comment}
                                onChange={(event) => updateReviewDraft(booking.id, "comment", event.target.value)}
                                placeholder={isAr ? "اكتب تعليقك عن الاستشارة" : "Write your consultation review"}
                                disabled={actionBookingId === booking.id}
                              />
                              <button
                                type="button"
                                className="btn btn-dark rounded-pill"
                                onClick={() => handleSubmitReview(booking)}
                                disabled={actionBookingId === booking.id}
                              >
                                {actionBookingId === booking.id
                                  ? isAr
                                    ? "جارٍ الإرسال..."
                                    : "Submitting..."
                                  : isAr
                                    ? "إرسال التقييم"
                                    : "Submit Review"}
                              </button>
                            </div>
                          ) : (
                            <div className="text-muted small">
                              {isAr
                                ? "يمكنك إضافة تقييم بعد اكتمال الاستشارة."
                                : "You can add a review after the consultation is completed."}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
